import { createReducer } from "@reduxjs/toolkit";

import {
  setProfile,
  getProfile,
  getProfileSuccess,
  getProfileFailure,
  updateProfile,
  updateProfileSuccess,
  updateProfileFailure,
  clearProfileStates,
  uploadAvatar,
  uploadAvatarSuccess,
  uploadAvatarFailure,
  deleteAvatar,
  deleteAvatarSuccess,
  deleteAvatarFailure,
  uploadBannerSuccess,
  uploadBannerFailure,
  uploadBanner,
  deleteBanner,
  deleteBannerSuccess,
  deleteBannerFailure,
  clearAvatarSuccess,
  clearAvatarError,
  setMySubscription,
  addMySubscription,
  removeMySubscription,
  reorderSubscriptions,
  incrementPostsCounter,
  decrementPostsCounter,
  disconnectMetamask,
  disconnectMetamaskSuccess,
  disconnectMetamaskFailure,
  disconnectPayoutMethod,
  disconnectPayoutMethodSuccess,
  disconnectPayoutMethodFailure,
  disconnectPayPal,
  disconnectPayPalSuccess,
  disconnectPayPalFailure,
  deleteAccount,
  deleteAccountSuccess,
  deleteAccountFailure,
  setContentShareTourSuccess,
  setContentShareTourFailure,
  resetProfileActiveStream,
  resetProfileUpcomingStream,
} from "./actions";
import { move } from "@helpers/array";
import { addMetamaskSuccess } from "../addMetamask/actions";
import { addPayPalSuccess } from "../addPayPal/actions";
import { signInSuccess } from "../signIn/actions";
import { createManualVerification } from "../manualVerification/actions";
import { Statuses } from "@hooks/useKyc";
import { likePostSuccess, unlikePostSuccess } from "../posts/actions";

const initialState = {
  id: undefined,
  createdAt: undefined,
  updatedAt: undefined,
  username: undefined,
  email: undefined,
  name: undefined,
  bio: undefined,
  dob: undefined,
  country: undefined,
  socialLinks: [],
  avatar: undefined,
  banner: undefined,
  subscriptions: [], // ids of my subscriptions
  followersCount: undefined,
  followingsCount: undefined,
  promotersCount: undefined,
  supportersCount: undefined,
  postsCount: undefined,
  likesCount: undefined,
  isVerified: undefined,
  isMetamaskConnected: undefined,
  isPayoutMethodLinked: undefined,
  isPayPalLinked: undefined,
  is2FAEnabled: undefined,
  isFirstLogin: undefined,
  error: null,
  isLoading: false,
  isSuccess: false,
  isAvatarLoading: false,
  isAvatarUploadSuccess: false,
  uploadAvatarError: null,
  isDeleting: false,
  isDeleted: false,
  deleteAvatarError: null,
  isBannerLoading: false,
  isBannerUploadSuccess: false,
  uploadBannerError: null,
  isBannerDeleting: false,
  deleteBannerError: null,
  totalSubscriptions: undefined,
  payoutMethodAccountId: undefined,
  paypalAccountId: undefined,
  paypalAccountEmail: undefined,
  metamaskWallet: undefined,
  isMetamaskDisconnecting: false,
  isPayoutMethodDisconnecting: false,
  isPayPalDisconnecting: false,
  isProfileDeleting: false,
  isProfileDeleted: false,
  isContentShareTourModelClosed: false,
  hasSubscription: undefined,
  payoutMethod: null,
  isProfileTourCompleted: false,
  activeStream: undefined,
  isStreamAllowed: false,
  isContentRequestAllowed: false,
  allPosts: [],
  postById: {},
  isLoadingNext: false,
  isLoadingFirstTime: false,
  upcomingStream: undefined,
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(unlikePostSuccess.type, (state, { payload }) => ({
      ...state,
      likesCount: payload.isMyPost ? state.likesCount - 1 : state.likesCount,
    }))
    .addCase(likePostSuccess.type, (state, { payload }) => ({
      ...state,
      likesCount: payload.isMyPost ? state.likesCount + 1 : state.likesCount,
    }))
    .addCase(createManualVerification.type, (state) => ({
      ...state,
      kyc: {
        ...state?.kyc,
        status: Statuses.pending,
      },
    }))
    .addCase(setProfile.type, (state, action) => ({
      ...state,
      ...action.payload,
    }))
    .addCase(getProfile.type, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getProfileSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      isLoading: false,
    }))
    .addCase(getProfileFailure.type, (state, action) => ({
      ...state,
      isSuccess: false,
      isLoading: false,
      error: action.payload,
    }))
    .addCase(updateProfile.type, (state) => ({
      ...state,
      isSuccess: false,
      isLoading: true,
      error: initialState.error,
    }))
    .addCase(updateProfileSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      isSuccess: true,
      isLoading: false,
    }))
    .addCase(updateProfileFailure.type, (state, action) => ({
      ...state,
      isLoading: false,
      error: action.payload,
    }))
    .addCase(uploadAvatar.type, (state) => ({
      ...state,
      isAvatarUploadSuccess: false,
      isAvatarLoading: true,
    }))
    .addCase(uploadAvatarSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      isAvatarUploadSuccess: true,
      isAvatarLoading: false,
    }))
    .addCase(uploadAvatarFailure.type, (state, action) => ({
      ...state,
      isAvatarLoading: false,
      isAvatarUploadSuccess: false,
      uploadAvatarError: action.payload,
    }))
    .addCase(deleteAvatar.type, (state) => ({
      ...state,
      isDeleting: true,
      isDeleted: false,
    }))
    .addCase(deleteAvatarSuccess.type, (state) => ({
      ...state,
      isDeleting: false,
      isDeleted: true,
      avatar: initialState.avatar,
    }))
    .addCase(deleteAvatarFailure.type, (state, action) => ({
      ...state,
      isDeleting: false,
      isDeleted: false,
      deleteAvatarError: action.payload,
    }))
    .addCase(uploadBanner.type, (state) => ({
      ...state,
      isBannerUploadSuccess: false,
      isBannerLoading: true,
    }))
    .addCase(uploadBannerSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      isBannerLoading: false,
      uploadBannerError: initialState.uploadBannerError,
    }))
    .addCase(uploadBannerFailure.type, (state, action) => ({
      ...state,
      isBannerLoading: false,
      isBannerUploadSuccess: false,
      uploadBannerError: action.payload,
    }))
    .addCase(deleteBanner.type, (state) => ({
      ...state,
      isBannerDeleting: true,
    }))
    .addCase(deleteBannerSuccess.type, (state) => ({
      ...state,
      isBannerDeleting: false,
      deleteBannerError: initialState.deleteBannerError,
      banner: initialState.banner,
    }))
    .addCase(deleteBannerFailure.type, (state, action) => ({
      ...state,
      isBannerDeleting: false,
      deleteBannerError: action.payload,
    }))
    .addCase(clearAvatarSuccess.type, (state) => ({
      ...state,
      isAvatarUploadSuccess: false,
    }))
    .addCase(clearAvatarError.type, (state) => ({
      ...state,
      uploadAvatarError: initialState.uploadAvatarError,
    }))
    .addCase(setMySubscription.type, (state, { payload }) => ({
      ...state,
      subscriptions: payload.subscriptions,
      totalSubscriptions: payload.totalCount,
    }))
    .addCase(addMySubscription.type, (state, { payload }) => {
      const updatedSubscriptions = [...state.subscriptions];

      if (!payload.creation) {
        updatedSubscriptions.push(...payload.subscriptions);
      }

      return {
        ...state,
        subscriptions: updatedSubscriptions,
        totalSubscriptions: payload.totalCount,
        hasSubscription: true,
      };
    })
    .addCase(removeMySubscription.type, (state, { payload }) => ({
      ...state,
      subscriptions: state.subscriptions.filter((id) => id !== payload.id),
      totalSubscriptions: state.totalSubscriptions - 1,
    }))
    .addCase(reorderSubscriptions.type, (state, { payload }) => {
      const { oldIndex, newIndex } = payload;

      return {
        ...state,
        subscriptions: move(state.subscriptions, oldIndex, newIndex),
      };
    })
    .addCase(incrementPostsCounter.type, (state) => {
      state.postsCount += 1;
    })
    .addCase(decrementPostsCounter.type, (state) => {
      state.postsCount -= 1;
    })
    .addCase(addMetamaskSuccess.type, (state, { payload }) => {
      state.metamaskWallet = payload.address;
      state.isMetamaskConnected = true;
    })
    .addCase(disconnectMetamask.type, (state) => {
      state.isMetamaskDisconnecting = true;
    })
    .addCase(disconnectMetamaskSuccess.type, (state) => {
      state.isMetamaskConnected = false;
      state.metamaskWallet = initialState.metamaskWallet;
      state.isMetamaskDisconnecting = false;
    })
    .addCase(disconnectMetamaskFailure.type, (state) => {
      state.isMetamaskDisconnecting = false;
    })
    .addCase(disconnectPayoutMethod().type, (state) => {
      state.isPayoutMethodDisconnecting = true;
    })
    .addCase(disconnectPayoutMethodSuccess().type, (state) => {
      state.payoutMethod = initialState.payoutMethod;
    })
    .addCase(disconnectPayoutMethodFailure().type, (state) => {
      state.isPayoutMethodDisconnecting = false;
    })
    .addCase(addPayPalSuccess.type, (state, { payload }) => {
      state.paypalAccountId = payload.paypalAccountId;
      state.paypalAccountEmail = payload.paypalAccountEmail;
      state.isPayPalLinked = true;
    })
    .addCase(disconnectPayPal.type, (state) => {
      state.isPayPalDisconnecting = true;
    })
    .addCase(disconnectPayPalSuccess.type, (state) => {
      state.isPayPalLinked = false;
      state.paypalAccountId = initialState.paypalAccountId;
      state.paypalAccountEmail = initialState.paypalAccountEmail;
      state.isPayPalDisconnecting = false;
    })
    .addCase(disconnectPayPalFailure.type, (state) => {
      state.isPayPalDisconnecting = false;
    })
    .addCase(clearProfileStates.type, (state) => ({
      ...state,
      error: initialState.error,
      isLoading: false,
      isSuccess: false,
      isDeleting: false,
      isDeleted: false,
      isSubscriptionDeleting: false,
      deleteAvatarError: initialState.deleteAvatarError,
      isAvatarLoading: initialState.isAvatarLoading,
      isAvatarUploadSuccess: initialState.isAvatarUploadSuccess,
      uploadAvatarError: initialState.uploadAvatarError,
      isBannerLoading: initialState.isBannerLoading,
      isBannerUploadSuccess: initialState.isBannerUploadSuccess,
      uploadBannerError: initialState.uploadBannerError,
      isBannerDeleting: initialState.isBannerDeleting,
      deleteBannerError: initialState.deleteBannerError,
    }))
    .addCase(deleteAccount.type, (state) => {
      state.isProfileDeleting = true;
      state.isProfileDeleted = false;
    })
    .addCase(deleteAccountSuccess.type, (state) => {
      state.isProfileDeleting = false;
      state.isProfileDeleted = true;
    })
    .addCase(deleteAccountFailure.type, (state) => {
      state.isProfileDeleting = false;
      state.isProfileDeleted = false;
    })
    .addCase(setContentShareTourSuccess.type, (state) => {
      state.isContentShareTourModelClosed = true;
    })
    .addCase(setContentShareTourFailure.type, (state) => {
      state.isContentShareTourModelClosed = false;
    })
    .addCase(signInSuccess.type, (state, action) => {
      state.isFirstLogin = action.payload.isFirstLogin;
    })
    .addCase(resetProfileActiveStream.type, (state) => {
      state.activeStream = undefined;
    })
    .addCase(resetProfileUpcomingStream.type, (state) => {
      state.upcomingStream = undefined;
    });
});
